import type { Answer, Group, Poll, Set as PollSet, Question, QuestionDeck } from 'schema/poll';
import type { ICState } from 'schema/state';

export function selectQuestionIndex<T extends { questions: { id: string }[] }>(data: T, questionId: string) {
  return data.questions.findIndex(it => it.id === questionId);
}

export function selectQuestion<T extends { questions: { id: string }[] }>(
  data: T,
  questionId: string,
): T['questions'][number] {
  return data.questions[selectQuestionIndex(data, questionId)]!;
}
export function selectGroupQuestionIndex(data: Poll, groupId: string) {
  return data.questions.findIndex(it => getQuestionGroupId(it, data.questionDecks) === groupId);
}

export function selectGroupQuestionLastIndex(data: Poll, groupId: string) {
  return findLastIndex(data.questions, it => getQuestionGroupId(it, data.questionDecks) === groupId);
}

export function selectGroupIndex(data: { groups: { id: string }[] }, groupId: string) {
  return data.groups.findIndex(it => it.id === groupId);
}

export function selectGroup<T extends { id: string }>(data: { groups: T[] }, groupId: string): T {
  return data.groups[selectGroupIndex(data, groupId)]!;
}

export function selectGroupUnsafe(data: Poll, groupId: string): Group | undefined {
  return data.groups[selectGroupIndex(data, groupId)];
}

export function selectQuestionDeck(data: { questionDecks: Poll['questionDecks'] }, deckId: string): QuestionDeck {
  const deck = data.questionDecks.find(deck => deck.id === deckId);

  if (!deck) {
    throw new Error(`Deck with id = ${deckId} not found`);
  }

  return deck;
}

export function selectSet(data: Poll, setId: string): PollSet {
  const set = data.sets?.find(set => set.id === setId);

  if (!set) {
    throw new Error(`Set with id = ${setId} not found`);
  }

  return set;
}

export function selectQuestionBySetId(data: Poll, setId: string) {
  const question = data.questions.find(question => question.setId === setId);

  if (!question) return null;

  return question;
}

function findLastIndex<T extends unknown[]>(arr: T, pred: (t: T[number]) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (pred(arr[i])) return i;
  }
  return -1;
}

export function selectQuestionAnswers(data: Poll, questionId: string): Answer[] {
  const question = selectQuestion(data, questionId);

  if ('answers' in question.data) {
    return question.data.answers ?? [];
  }

  throw new Error(`Question with id = ${questionId} doesn\`t have answers`);
}

export function selectFinalIndex(data: Poll, finalId: string) {
  return data.finals.findIndex(it => it.id === finalId);
}

export function selectFinal(data: Poll, finalId: string) {
  return data.finals[selectFinalIndex(data, finalId)];
}

export function isFinalWithZeroAutoClose(data: Poll, finalId: string | null) {
  const final = finalId ? selectFinal(data, finalId) : null;
  return !!final?.data.hasAutoClose && !final.data.autoCloseMs;
}

export function selectWelcome(data: Poll, welcomeId: string) {
  const welcomeList = data.welcome || [];
  const welcomeIndex = welcomeList.findIndex(welcome => welcome.id === welcomeId);
  return welcomeList[welcomeIndex];
}

export function selectGroupFirstQuestion(data: Poll, groupId: string) {
  return data.questions.find(it => getQuestionGroupId(it, data.questionDecks) === groupId);
}

/** Возвращает идентификаторы cтраниц из сета */
export function selectSetScreens(data: Poll, setId: string) {
  const groupIdsSet = new Set<Group['id']>();

  const result: { type: 'question' | 'group'; id: string }[] = [];

  data.questions.forEach(question => {
    /** Если вопрос в группе, то setId берется из группы */
    const groupId = getQuestionGroupId(question, data.questionDecks);
    if (groupId) {
      const group = selectGroup(data, groupId);
      if (group.setId === setId && !groupIdsSet.has(group.id)) {
        groupIdsSet.add(group.id);
        result.push({ type: 'group', id: group.id });
      }
    } else if (question.setId === setId) {
      result.push({ type: 'question', id: question.id });
    }
  });

  return result;
}

/**
 * Если группа сейчас показывается, то вернуть только вопросы в группе, которые показываются
 * (например, из-за `group.data.hasSequentialDisplay`/`question.data.logicRules`).
 *
 * Иначе если группа не показывается (уже пройдена или ещё впереди), то вернуть все вопросы as is.
 */
export function selectDisplayedOrAllGroupQuestions<Q extends { id: string; groupId?: string; deckId?: string }>(
  data: {
    questions: Q[];
    questionDecks: Poll['questionDecks'];
  },
  groupId: string,
  stack: ICState['answersStack'],
) {
  const displayedQuestionFilter = getGrouplessOrDisplayedOrAllQuestionsFilter(stack, data);
  return selectGroupQuestions(data, groupId).filter(displayedQuestionFilter);
}

export function selectDisplayedQuestions({ state, poll }: { state: ICState; poll: Poll }) {
  const { groupId, questionId } = state;

  if (groupId) {
    return selectDisplayedOrAllGroupQuestions(poll, groupId, state.answersStack);
  } else if (questionId) {
    return [selectQuestion(poll, questionId)];
  }

  return [];
}

function getGrouplessOrDisplayedOrAllQuestionsFilter(
  stack?: ICState['answersStack'],
  poll?: { questions?: { id: string; groupId?: string; deckId?: string }[]; questionDecks?: Poll['questionDecks'] },
) {
  let displayedGroupId: string | null = null;
  const displayedGroupQuestionsIds: Set<string> = new Set();

  const displayedItem = stack?.at(-1);
  if (stack?.length && poll?.questions?.length && displayedItem) {
    const displayedQuestion = poll?.questions.find(it => it.id === displayedItem.id);
    const groupId =
      displayedQuestion && poll.questionDecks ? getQuestionGroupId(displayedQuestion, poll.questionDecks) : undefined;

    if (groupId && displayedQuestion) {
      displayedGroupId = groupId;
      displayedGroupQuestionsIds.add(displayedQuestion.id);

      for (let i = stack.length - 1; i >= 0; i--) {
        const found = poll.questions.find(it => it.id === stack[i]?.id);
        const foundGroupId = found && poll.questionDecks ? getQuestionGroupId(found, poll.questionDecks) : undefined;
        if (foundGroupId !== displayedGroupId) break;
        if (found) displayedGroupQuestionsIds.add(found.id);
      }
    }
  }

  return (item: { id: string; groupId?: string }) => {
    const groupId = poll?.questionDecks ? getQuestionGroupId(item, poll.questionDecks) : undefined;
    const groupless = !groupId;
    const inDisplayedGroup = displayedGroupId && groupId === displayedGroupId;
    if (groupless) {
      return true;
    } else if (inDisplayedGroup) {
      return displayedGroupQuestionsIds.has(item.id);
    }
    return true;
  };
}

export const sortGroupQuestionsByStack = (groupId: string, poll: Poll, stack: ICState['answersStack']) => {
  const result: Question[] = [];

  for (let i = stack.length - 1; i >= 0; i--) {
    const item = stack[i];
    if (!item) break;

    const found = poll.questions.find(question => question.id === item.id);
    const foundGroupId = found ? getQuestionGroupId(found, poll.questionDecks) : undefined;
    if (!found || foundGroupId !== groupId) break;

    result.push(found);
  }

  return result.reverse();
};

export function selectGroupQuestions<
  Q extends {
    groupId?: string;
    deckId?: string;
  },
>(
  poll: {
    questions: Q[];
    questionDecks: Poll['questionDecks'];
  },
  targetGroupId: string,
) {
  return poll.questions.filter(q => getQuestionGroupId(q, poll.questionDecks) === targetGroupId);
}

export const getQuestionGroupId = (question: { groupId?: string; deckId?: string }, questionDecks: QuestionDeck[]) => {
  if (question.deckId) {
    return questionDecks.find(deck => deck.id === question.deckId)?.groupId;
  } else if (question.groupId) {
    return question.groupId;
  }
  return;
};

export const getQuestionSetId = (question: Question, poll: Poll) => {
  const groupId = getQuestionGroupId(question, poll.questionDecks);
  if (groupId) {
    const group = selectGroup(poll, groupId);
    return group?.setId;
  }
  return question.setId;
};

export const selectGroupQuestionsIds = (poll: Poll, targetGroupId: string) => {
  return selectGroupQuestions(poll, targetGroupId).map(question => question.id);
};
/** Возвращает "релевантные" вопросы из показываемых на экране.
 *
 * Т.е. вопросы, состояние которых ещё возможно изменить:
 * - одиночный вопрос
 * - все вопросы в обычной группе
 * - последний вопрос в группе с `hasSequentialDisplay`
 */
export function selectRelevantQuestions({ state, poll }: { state: ICState; poll: Poll }) {
  const { groupId, questionId } = state;

  if (groupId) {
    const group = selectGroup(poll, groupId);

    if (group.data.hasSequentialDisplay) {
      const { answersStack } = state;
      const { id: lastQuestionId } = answersStack[answersStack.length - 1]!;
      const lastQuestion = selectQuestion(poll, lastQuestionId);

      return [lastQuestion];
    }
    return selectDisplayedOrAllGroupQuestions(poll, groupId, state.answersStack);
  } else if (questionId) {
    return [selectQuestion(poll, questionId)];
  }

  return [];
}
